import "zod/compile";
import { oc } from "@orpc/contract";
import { z } from "zod";
import { PurchaseUploadListItemSchema } from "./purchase-upload.contract";

export const PurchaseTrendLineTypeSchema = z.enum([
  "estimates",
  "predictions",
  "reported",
]);

export type PurchaseTrendLineType = z.infer<typeof PurchaseTrendLineTypeSchema>;

const YearSchema = z.number().int().min(2000).max(2100);
const MonthKeySchema = z.string().regex(/^\d{4}-\d{2}$/);

export const PurchaseTrendRequestSchema = z
  .object({
    year: YearSchema.optional(),
    startYear: YearSchema.optional(),
    endYear: YearSchema.optional(),
    startMonth: MonthKeySchema.optional(),
    endMonth: MonthKeySchema.optional(),
    supplierId: z.string().uuid().optional(),
    supplierIds: z.array(z.string().uuid()).optional(),
    divisionIds: z.array(z.string().uuid()).optional(),
    lineType: PurchaseTrendLineTypeSchema.optional(),
    uploadId: z.string().uuid().optional(),
  })
  .refine(
    (input) => {
      if (input.startMonth !== undefined || input.endMonth !== undefined) {
        return input.startMonth !== undefined && input.endMonth !== undefined;
      }
      if (input.startYear !== undefined || input.endYear !== undefined) {
        return input.startYear !== undefined && input.endYear !== undefined;
      }
      return input.year !== undefined;
    },
    {
      message:
        "Provide year, startYear and endYear, or startMonth and endMonth",
    },
  )
  .refine(
    (input) =>
      input.supplierId === undefined || input.supplierIds === undefined,
    {
      message: "Provide supplierId or supplierIds, not both",
    },
  );

export type PurchaseTrendRequest = z.infer<typeof PurchaseTrendRequestSchema>;

export const PurchaseTrendMonthKindSchema = z.enum(["actual", "forecast"]);

export type PurchaseTrendMonthKind = z.infer<
  typeof PurchaseTrendMonthKindSchema
>;

export const PurchaseTrendForecastUnavailableReasonSchema = z.enum([
  "no_complete_months",
  "prior_year_ytd_zero",
  "no_prior_year_amounts",
]);

export type PurchaseTrendForecastUnavailableReason = z.infer<
  typeof PurchaseTrendForecastUnavailableReasonSchema
>;

export const PurchaseTrendForecastSchema = z.object({
  growthFactor: z.number().nullable(),
  unavailableReason: PurchaseTrendForecastUnavailableReasonSchema.nullable(),
  lastCompleteMonth: z.number().int().min(1).max(12).nullable(),
  lastActualMonth: z.number().int().min(1).max(12).nullable(),
});

export type PurchaseTrendForecast = z.infer<typeof PurchaseTrendForecastSchema>;

export const PurchaseTrendMonthValuesSchema = z.object({
  year: z.number().int(),
  month: z.number().int().min(1).max(12),
  purchases: z.object({
    currentYearAmount: z.number(),
    priorYearAmount: z.number(),
    forecastAmount: z.number().nullable(),
    kind: PurchaseTrendMonthKindSchema,
    isPartialMonth: z.boolean(),
  }),
  reportedVolume: z.number(),
  rebateEarnings: z.number(),
});

export type PurchaseTrendMonthValues = z.infer<
  typeof PurchaseTrendMonthValuesSchema
>;

export const PurchaseTrendSupplierSeriesSchema = z.object({
  supplierId: z.string().uuid(),
  supplierName: z.string(),
  forecast: PurchaseTrendForecastSchema,
  months: z.array(PurchaseTrendMonthValuesSchema),
});

export type PurchaseTrendSupplierSeries = z.infer<
  typeof PurchaseTrendSupplierSeriesSchema
>;

export const PurchaseTrendUnmatchedSupplierSchema = z.object({
  supplierCode: z.string(),
  supplierName: z.string(),
  currentYearTotal: z.number(),
  priorYearTotal: z.number(),
});

export const PurchaseTrendAppliedMappingSchema = z.object({
  kind: z.enum(["division", "supplier"]),
  sourceName: z.string(),
  sourceBranch: z.string().nullable(),
  sourceCode: z.string().nullable(),
  targetId: z.string().uuid(),
  targetName: z.string(),
});

export type PurchaseTrendAppliedMapping = z.infer<
  typeof PurchaseTrendAppliedMappingSchema
>;

export type PurchaseTrendUnmatchedSupplier = z.infer<
  typeof PurchaseTrendUnmatchedSupplierSchema
>;

export const PurchaseTrendCellSchema = z.object({
  year: z.number().int(),
  month: z.number().int().min(1).max(12),
  supplierId: z.string().uuid().nullable(),
  supplierName: z.string(),
  supplierCode: z.string().nullable(),
  divisionId: z.string().uuid().nullable(),
  divisionName: z.string(),
  notInGentlySupplier: z.boolean(),
  notInGentlyDivision: z.boolean(),
  purchases: z.number().nullable(),
  reportedRebates: z.number().nullable(),
});

export type PurchaseTrendCell = z.infer<typeof PurchaseTrendCellSchema>;

export const PurchaseTrendResponseSchema = z.object({
  startYear: z.number().int(),
  startMonth: z.number().int().min(1).max(12),
  endYear: z.number().int(),
  endMonth: z.number().int().min(1).max(12),
  lineType: PurchaseTrendLineTypeSchema,
  reportedAlgorithm: z.string().nullable(),
  months: z.array(PurchaseTrendMonthValuesSchema),
  forecast: PurchaseTrendForecastSchema,
  suppliers: z.array(PurchaseTrendSupplierSeriesSchema),
  unmatchedPurchaseSuppliers: z.array(PurchaseTrendUnmatchedSupplierSchema),
  purchaseSuppliersWithoutRebateAgreement: z.array(
    PurchaseTrendUnmatchedSupplierSchema,
  ),
  unmatchedDivisionNames: z.array(z.string()),
  unmatchedPurchaseDivisions: z
    .array(
      z.object({
        divisionName: z.string(),
        branch: z.string().nullable(),
      }),
    )
    .optional(),
  appliedPurchaseMappings: z
    .array(PurchaseTrendAppliedMappingSchema)
    .optional(),
  cells: z.array(PurchaseTrendCellSchema),
  uploadsInView: z.array(PurchaseUploadListItemSchema),
  scopedDivisions: z
    .array(z.object({ id: z.string().uuid(), name: z.string() }))
    .optional(),
});

export type PurchaseTrendResponse = z.infer<typeof PurchaseTrendResponseSchema>;

const get = oc
  .input(PurchaseTrendRequestSchema)
  .output(z.object({ data: PurchaseTrendResponseSchema }))
  .route({
    method: "GET",
    path: "/purchase-trend",
    summary: "Purchases versus rebates trend",
    description:
      "Returns monthly uploaded purchases, supplier-reported volume, and rebate earnings for rebate suppliers. Purchases come from the selected upload when uploadId is provided, otherwise the newest upload for each year. Volume is summed from active invoice line items. Earnings reuse the stored-breakdown report path.",
  });

export const purchaseTrendContracts = {
  get,
} as const;
